import { IStep } from './general';

export enum ProductFields {
  ID = 'id',
  NAME = 'name',
  DESCRIPTION = 'description',
  CATEGORYID = 'categoryId',
  PRICEPERUNIT = 'pricePerUnit',
  BILLINGFREQUENCY = 'billingFrequency',
  RECURRENTREGULARBILLINGFREQUENCY = 'recurrentRegularBillingFrequency',
  CUSTOMBILLINGTRIGGER = 'customBillingTrigger',
  EVERY = 'every',
  BILLINGFREQUENCYPERIODFOREVERY = 'billingFrequencyPeriodForEvery',
  OWNER = 'owner',
  CREATEDAT = 'createdAt',
  LASTUPDATEDBY = 'lastUpdatedBy',
  LASTUPDATEDAT = 'lastUpdatedAt',
  CATEGORY = 'category',
  STATUS = 'status',
  VERSION = 'version',
  BILLING_TRIGGER_ID = 'billingTriggerId',
  BILLING_TRIGGER = 'billingTrigger',
  QUICKBOOKS_ACCOUNT_ID = 'quickBooksAccountId',
}

export type ProductCategory = {
  id: string;
  name: string;
};

export enum BillingEngineRuleInvoiceSchedule {
  None = 0,
  Immediate = 1,
  Weekly = 2,
  Monthly = 3,
  Daily = 4,
  Yearly = 5,
}

export enum BillingFrequency {
  OneTime = 1,
  RecurrentRegular = 2,
  RecurrentIrregular = 3,
}

export enum RecurrentRegularBillingFrequency {
  Daily = 1,
  Weekly = 2,
  BiWeekly = 3,
  Monthly = 4,
  Annually = 5,
  Every = 6,
}

export enum BillingFrequencyPeriodForEvery {
  Days = 1,
  Weeks = 2,
  Months = 3,
  Years = 4,
}

export enum ProductStatus {
  Unknown = 0,
  Active = 1,
  Inactive = 2,
  Retired = 3,
}

export interface IProduct {
  [ProductFields.ID]: string;
  [ProductFields.NAME]: string;
  [ProductFields.DESCRIPTION]: string;
  [ProductFields.CATEGORYID]: string;
  [ProductFields.PRICEPERUNIT]: number;
  [ProductFields.BILLINGFREQUENCY]: BillingFrequency;
  [ProductFields.RECURRENTREGULARBILLINGFREQUENCY]: RecurrentRegularBillingFrequency;
  [ProductFields.CUSTOMBILLINGTRIGGER]: string;
  [ProductFields.EVERY]: string;
  [ProductFields.LASTUPDATEDAT]?: string;
  [ProductFields.LASTUPDATEDBY]?: string;
  [ProductFields.BILLINGFREQUENCYPERIODFOREVERY]: BillingFrequencyPeriodForEvery;
  [ProductFields.CATEGORY]: ProductCategory;
  [ProductFields.STATUS]: ProductStatus;
  [ProductFields.VERSION]?: string;
  [ProductFields.BILLING_TRIGGER_ID]?: string;
  [ProductFields.BILLING_TRIGGER]?: any;
  [ProductFields.QUICKBOOKS_ACCOUNT_ID]: string;
}

export enum ProductStep {
  GENERAL_INFORMATION = 'general-information',
  BILLING = 'billing',
  REVIEW = 'review',
}

export interface IStepMap {
  [key: string]: IStep;
}

export const productStepMap: IStepMap = {
  [ProductStep.GENERAL_INFORMATION]: {
    key: ProductStep.GENERAL_INFORMATION,
    title: 'General Information',
    subtitle: 'Create New Product',
    hideControls: false,
    order: 0,
    fields: [
      { name: ProductFields.NAME, required: true },
      { name: ProductFields.CATEGORY, required: true },
      { name: ProductFields.DESCRIPTION, required: false },
      { name: ProductFields.BILLINGFREQUENCY, required: true },
      { name: ProductFields.CUSTOMBILLINGTRIGGER, required: false },
      { name: ProductFields.RECURRENTREGULARBILLINGFREQUENCY, required: true },
      { name: ProductFields.BILLINGFREQUENCYPERIODFOREVERY, required: false },
      { name: ProductFields.EVERY, required: false },
    ],
  },
};

export const getFallbackProduct = (): IProduct => ({
  [ProductFields.ID]: '',
  [ProductFields.NAME]: '',
  [ProductFields.DESCRIPTION]: '',
  [ProductFields.CATEGORYID]: '',
  [ProductFields.PRICEPERUNIT]: null,
  [ProductFields.BILLINGFREQUENCY]: null,
  [ProductFields.RECURRENTREGULARBILLINGFREQUENCY]: null,
  [ProductFields.CUSTOMBILLINGTRIGGER]: null,
  [ProductFields.EVERY]: '',
  [ProductFields.BILLINGFREQUENCYPERIODFOREVERY]: null,
  [ProductFields.CATEGORY]: null,
  [ProductFields.STATUS]: 0,
  [ProductFields.QUICKBOOKS_ACCOUNT_ID]: '',
});

export const billingStatusMap = {
  [BillingFrequency.OneTime]: 'One Time',
  [BillingFrequency.RecurrentIrregular]: 'Recurrent Irregular',
  [BillingFrequency.RecurrentRegular]: 'Recurrent Regular',
};

export const recurrentRegularBillingFrequencyMap = {
  [RecurrentRegularBillingFrequency.Daily]: 'Daily',
  [RecurrentRegularBillingFrequency.Weekly]: 'Weekly',
  [RecurrentRegularBillingFrequency.BiWeekly]: 'BiWeekly',
  [RecurrentRegularBillingFrequency.Monthly]: 'Monthly',
  [RecurrentRegularBillingFrequency.Annually]: 'Annually',
  [RecurrentRegularBillingFrequency.Every]: 'Every',
};

export const billingFrequencyPeriodForEveryMap = {
  [BillingFrequencyPeriodForEvery.Days]: 'Days',
  [BillingFrequencyPeriodForEvery.Weeks]: 'Weeks',
  [BillingFrequencyPeriodForEvery.Months]: 'Months',
  [BillingFrequencyPeriodForEvery.Years]: 'Years',
};
